import React, { useState } from 'react';
import { Coupon } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';

// Coupon Master (multi_coupon_layout.xml + Coupon app settings). Coupons are
// applied on the Bill screen (a coupon field appears there once any exist).
const blank = {
  code: '',
  type: 'percent' as 'percent' | 'flat',
  value: '',
  minBill: '',
  expiry: '',
  active: true,
};

const Coupons: React.FC = () => {
  const { coupons, addCoupon, updateCoupon, deleteCoupon, config } = usePos();
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [f, setF] = useState({ ...blank });

  const set = (k: keyof typeof blank, v: string | boolean) => setF((prev) => ({ ...prev, [k]: v }));

  const reset = () => {
    setEditing(null);
    setF({ ...blank });
  };

  const save = () => {
    if (!f.code.trim()) return alert('Coupon code is required.');
    if (!(parseFloat(f.value) > 0)) return alert('Enter a coupon value greater than 0.');
    const data: Omit<Coupon, 'id'> = {
      code: f.code.trim().toUpperCase(),
      type: f.type,
      value: parseFloat(f.value),
      minBill: f.minBill ? parseFloat(f.minBill) : undefined,
      expiry: f.expiry || undefined,
      active: f.active,
    };
    if (editing) updateCoupon({ ...editing, ...data });
    else addCoupon(data);
    reset();
  };

  const edit = (c: Coupon) => {
    setEditing(c);
    setF({
      code: c.code,
      type: c.type,
      value: String(c.value),
      minBill: c.minBill != null ? String(c.minBill) : '',
      expiry: c.expiry ?? '',
      active: c.active,
    });
  };

  const remove = () => {
    if (editing && window.confirm('Delete this coupon?')) {
      deleteCoupon(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;
  const valueLabel = (c: Coupon) => (c.type === 'percent' ? `${c.value}%` : `${config.currencySymbol}${c.value}`);
  const expired = (c: Coupon) => !!c.expiry && new Date(c.expiry + 'T23:59:59') < new Date();

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <div className="pm-list-scroll">
          {coupons.map((c) => (
            <button key={c.id} className={`pm-list-item ${editing?.id === c.id ? 'sel' : ''}`} onClick={() => edit(c)}>
              <span className="pm-li-name">
                {c.code} <span style={{ fontWeight: 400 }}>· {valueLabel(c)} off</span>
              </span>
              <span className="pm-li-meta">
                {!c.active ? 'Inactive' : expired(c) ? 'Expired' : 'Active'}
                {c.minBill ? ` · min ${config.currencySymbol}${c.minBill}` : ''}
                {c.expiry ? ` · till ${c.expiry}` : ''}
              </span>
            </button>
          ))}
          {coupons.length === 0 && <p className="muted" style={{ padding: 16 }}>No coupons. Create one to enable coupon entry on the Bill screen.</p>}
        </div>
      </div>

      {/* form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>{editing ? 'Edit Coupon' : 'New Coupon'}</span>
          <div className="pf-top-btns">
            {editing && <button className="pf-btn-orange" onClick={reset}>New</button>}
            {editing && <button className="pf-btn-orange" onClick={remove}>Delete</button>}
            <button className="pf-btn-orange" onClick={save}>{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r2">
            <div>{lbl('Coupon Code *')}<input value={f.code} onChange={(e) => set('code', e.target.value)} placeholder="e.g. SAVE10" /></div>
            <div>
              {lbl('Type')}
              <select value={f.type} onChange={(e) => set('type', e.target.value as 'percent' | 'flat')}>
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat ({config.currencySymbol})</option>
              </select>
            </div>
          </div>
          <div className="pf-row r2">
            <div>{lbl(f.type === 'percent' ? 'Value (%) *' : `Value (${config.currencySymbol}) *`)}<input type="number" min={0} value={f.value} onChange={(e) => set('value', e.target.value)} /></div>
            <div>{lbl('Minimum Bill')}<input type="number" min={0} value={f.minBill} onChange={(e) => set('minBill', e.target.value)} /></div>
          </div>
          <div className="pf-row r2">
            <div>{lbl('Expiry Date')}<input type="date" value={f.expiry} onChange={(e) => set('expiry', e.target.value)} /></div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Active
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Coupons;
